import { useCallback, useMemo, useState } from 'react';

interface UsePaginatedList<T> {
  items: T[];
  perPage: number;
}

export const usePaginatedList = <T>({
  items,
  perPage,
}: UsePaginatedList<T>) => {
  const [activePage, setActivePage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(items.length / perPage));

  const nextPage = useCallback(() => {
    setActivePage((prev) => Math.min(prev + 1, totalPages));
  }, [totalPages]);

  const prevPage = useCallback(() => {
    setActivePage((prev) => Math.max(prev - 1, 1));
  }, []);

  const setCurrentPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setActivePage(page);
      }
    },
    [totalPages],
  );

  const currentPageItems = useMemo(() => {
    const start = (activePage - 1) * perPage;
    return items.slice(start, start + perPage);
  }, [activePage, perPage, items]);

  return {
    activePage,
    totalPages,
    nextPage,
    prevPage,
    setCurrentPage,
    items: currentPageItems,
  };
};
